import { useMemo, useState, useCallback } from 'react';

function resolveValue(row, key) {
  if (row == null) return null;
  if (typeof key === 'function') return key(row);
  if (typeof key !== 'string') return row[key];
  if (!key.includes('.')) return row[key];
  return key.split('.').reduce((acc, part) => (acc == null ? undefined : acc[part]), row);
}

function compareValues(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date || b instanceof Date) {
    const ta = a instanceof Date ? a.getTime() : Date.parse(a);
    const tb = b instanceof Date ? b.getTime() : Date.parse(b);
    if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;
  }
  const na = typeof a === 'number' ? a : Number(String(a).replace(',', '.'));
  const nb = typeof b === 'number' ? b : Number(String(b).replace(',', '.'));
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a).trim() !== '' && String(b).trim() !== '') {
    if (String(a).match(/^[\d.,\-]+$/) && String(b).match(/^[\d.,\-]+$/)) {
      return na - nb;
    }
  }
  return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base', numeric: true });
}

/**
 * Ordenação client-side para tabelas MUI.
 * @param {Array} rows
 * @param {string|null} initialKey
 * @param {'asc'|'desc'} initialDir
 * @param {Record<string, function>} [accessors] - getters opcionais por coluna
 */
export function useClientSort(rows, initialKey = null, initialDir = 'asc', accessors = null) {
  const [orderBy, setOrderBy] = useState(initialKey);
  const [order, setOrder] = useState(initialDir);

  const requestSort = useCallback((key) => {
    setOrderBy((prev) => {
      if (prev === key) {
        setOrder((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setOrder('asc');
      return key;
    });
  }, []);

  const sorted = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (!orderBy) return list;
    const accessor = accessors?.[orderBy] || orderBy;
    const copy = [...list];
    copy.sort((ra, rb) => {
      const cmp = compareValues(resolveValue(ra, accessor), resolveValue(rb, accessor));
      return order === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, orderBy, order, accessors]);

  return { sorted, orderBy, order, requestSort, setOrderBy, setOrder };
}

export { resolveValue, compareValues };
